// Layer 1 (Data Schema): the session — a cursor on the seeded series plus the
// perturbations laid over them. Shape only; transitions live in ScenarioEngine.
//
// Before B5 the scenario was three prose states behind a button. Now the
// engine's state is `SessionState`; `ScenarioPhase` survives as a DERIVED
// value (`phaseFor(state)`) so every `useScenario().phase` consumer keeps its
// contract while the numbers underneath come from the series (#197).

import { DEFAULT_CURSOR, type SeriesTime } from './reading-series';
import type { PlantCadence } from './unit-operation';

/** The hardness walkthrough's three reads. Derived, never stored:
 * idle = the hardness bundle is off · provisional = on, cursor before the
 * confirming evidence lands · confirmed = on, cursor at or after it. */
export type ScenarioPhase = 'idle' | 'provisional' | 'confirmed';

/** Named perturbation bundles the demo can switch on. */
export type ScenarioId = 'hardness-step-change' | 'silver-push' | 'media-depletion';

/**
 * A change laid over one series from a moment onward. Evaluated per stamp for
 * history and at the cursor for "now". Additive kinds compose; `set` wins.
 *   ramp   value += perHour × hours since `from`, capped at `cap` when given
 *   step   value += delta from `from`
 *   set    value  = value from `from` (an evidence landing, e.g. SA01 = 88 µm)
 *   scale  value ×= factor from `from` (the what-if sliders)
 */
export type Perturbation =
  | { kind: 'ramp'; seriesId: string; from: SeriesTime; perHour: number; cap?: number }
  | { kind: 'step'; seriesId: string; from: SeriesTime; delta: number }
  | { kind: 'set'; seriesId: string; from: SeriesTime; value: number }
  | { kind: 'scale'; seriesId: string; from: SeriesTime; factor: number };

export type ScenarioBundle = {
  id: ScenarioId;
  /** Chip label as the plant-model scenario control shows it. */
  label: string;
  /** One line: what the plant is doing and why it matters. */
  description: string;
  perturbations: readonly Perturbation[];
  /**
   * Walkthrough stops, when the bundle is played by the advance button:
   * `provisionalAt` is where the cursor lands on the first advance (the
   * on-line signal is already moving); `confirmsAt` is when the confirming
   * evidence lands (the second advance). Bundles without stops are toggles.
   */
  stops?: { provisionalAt: SeriesTime; confirmsAt: SeriesTime };
  /** `[syn]` — every bundle is a seeded story; cite the source that shaped it. */
  provenance: string;
};

/** The #162 sliders — a user-driven perturbation on WIT-021 and ore hardness.
 * Lives here (not in ProcessGraph.transform) because the engine owns it now. */
export type WhatIfInputs = {
  /** Mill feed, stph — §8.2 normal band 55–90, design 75. */
  feedStph: number;
  /** Ore hardness as ×design, 0.85–1.25. */
  hardness: number;
};

export const WHAT_IF_DEFAULTS: WhatIfInputs = { feedStph: 75, hardness: 1 };

/**
 * The whole session in one value. Refresh resets.
 * Invariant: `whatIf` and `scenarios` are mutually exclusive — opening the
 * what-if popover clears the scenarios; switching a scenario on clears what-if
 * (the shipped #162 rule, kept).
 */
export type SessionState = {
  cursor: SeriesTime;
  scenarios: readonly ScenarioId[];
  whatIf: WhatIfInputs | null;
  /** route-reads paul-call.A8 #256 -- plant sample cadence, default 'lab'. */
  cadence: PlantCadence;
};

/** The session on load. Lives in the schema (not the engine) so seeds and
 * derive modules can name it without importing an engine — the B6 import
 * cycle (`ScenarioEngine → DiagnosisEngine → seed → ScenarioEngine`) is cut here. */
export const INITIAL_SESSION_STATE: SessionState = {
  cursor: DEFAULT_CURSOR,
  scenarios: [],
  whatIf: null,
  cadence: 'lab',
};
